import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  PaginationState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { ExternalLink, FileText, Info } from 'lucide-react';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { IndexPath } from '@/Config/app/router/index.path';
import {
  ArticleUsageStatsDto,
  KBSourcesStatsDto,
  SonyArticleSourceTypeDto,
} from '@/Domain/api/Analytics/dto/KBSources.dto';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { CardContent } from '@/Domain/components/Shared/Card/Card';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/Domain/components/Shared/Empty/Empty';
import {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/Domain/components/Shared/Pagination/pagination';
import { Skeleton } from '@/Domain/components/Shared/Skeleton/Skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/Domain/components/Shared/Table/Table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { SortAscIcon } from '@/Domain/components/icons/SortAsc.icon';
import { SortDescIcon } from '@/Domain/components/icons/SortDesc.icon';
import { PaginationHelper } from '@/Domain/helper/Pagination.helper';
import { UsageSparkline } from '@/UI/Insights/KBSources/components/UsageSparkline';
import { formatUsageShare } from '@/UI/Insights/KBSources/usageShare';

type Props = {
  data: KBSourcesStatsDto | undefined;
  isLoading: boolean;
  isError: boolean;
  sorting: SortingState;
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  onViewExamples: (articleId: number) => void;
};

const getArticleTypeBadgeVariant = (type: string) => {
  switch (type) {
    case 'URL':
      return 'primary';
    case 'PDF':
      return 'secondary';
    case 'Article':
      return 'outline';
    default:
      return 'outline';
  }
};

const getSafeExternalUrl = (url: string | null): string | null => {
  if (!url) return null;
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
      ? parsedUrl.href
      : null;
  } catch {
    return null;
  }
};

export const KBSourcesTable = ({
  data,
  isLoading,
  isError,
  sorting,
  setSorting,
  pagination,
  setPagination,
  onViewExamples,
}: Props) => {
  const { t, i18n } = useTranslation('insights_page', { keyPrefix: 'kb_sources' });
  const { scenario } = useParams<{ scenario: string }>();

  const sonyArticleSourceLabels: Record<SonyArticleSourceTypeDto, string> = {
    support_article: t('table.source_types.support_article'),
    compatibility_article: t('table.source_types.compatibility_article'),
    quality_issue_article: t('table.source_types.quality_issue_article'),
    manual: t('table.source_types.manual'),
    help_guide: t('table.source_types.help_guide'),
    download: t('table.source_types.download'),
  };

  const totalPages = Math.ceil((data?.count ?? 0) / pagination.pageSize);
  const currentPage = pagination.pageIndex + 1;

  const pages = useMemo(
    () => PaginationHelper.getRange(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const columns: ColumnDef<ArticleUsageStatsDto>[] = [
    {
      accessorKey: 'article_title',
      header: () => t('table.article'),
      enableSorting: false,
      minSize: 300,
      cell: ({ row }) => {
        const title = row.original.article_title;
        const articleUrl = getSafeExternalUrl(row.original.article_url);
        const internalArticleUrl = `/i/${scenario}/${IndexPath.knowledgeBase.article.replace(
          ':id',
          String(row.original.article_id),
        )}`;
        return (
          <div className="flex flex-col gap-1">
            {articleUrl ? (
              <a
                data-testid={`kb-source-article-link-${row.original.article_id}`}
                href={articleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-start gap-1 rounded-sm text-info underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                <span className="line-clamp-2">{title}</span>
                <ExternalLink className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
              </a>
            ) : (
              <Link
                data-testid={`kb-source-kb-article-link-${row.original.article_id}`}
                to={internalArticleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="line-clamp-2 rounded-sm text-info underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                {title}
              </Link>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'article_type',
      header: () => t('table.type'),
      enableSorting: false,
      cell: ({ row }) => {
        // A subtype this bundle has no label for must not render an empty badge.
        const displayType =
          (row.original.article_source_type &&
            sonyArticleSourceLabels[row.original.article_source_type]) ||
          row.original.article_type;
        return (
          <Badge
            data-testid={`kb-source-article-type-${row.original.article_id}`}
            variant={getArticleTypeBadgeVariant(row.original.article_type)}
          >
            {displayType}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'raw_usage_count',
      header: () => t('table.usage_count'),
      enableSorting: true,
      meta: { align: 'right', tooltip: t('table.usage_count_tooltip') },
      cell: ({ row }) => (
        <span
          data-testid={`kb-source-usage-count-${row.original.article_id}`}
          className="font-medium tabular-nums"
        >
          {row.original.raw_usage_count.toLocaleString(i18n.language)}
        </span>
      ),
    },
    {
      accessorKey: 'usage_share_percent',
      header: () => t('table.usage_share'),
      enableSorting: false,
      meta: { align: 'right', tooltip: t('table.usage_share_tooltip') },
      cell: ({ row }) => (
        <div className="text-right">
          <span className="font-medium tabular-nums">
            {formatUsageShare(row.original.usage_share_percent, row.original.raw_usage_count)}
          </span>
        </div>
      ),
    },
    {
      id: 'usage_trend',
      header: () => t('table.trend'),
      enableSorting: false,
      meta: { align: 'center' },
      size: 180,
      cell: ({ row }) => <UsageSparkline data={row.original.usage_trend} />,
    },
    {
      id: 'actions',
      header: () => '',
      enableSorting: false,
      cell: ({ row }) => (
        <Button variant="ghost" size="sm" onClick={() => onViewExamples(row.original.article_id)}>
          {t('table.view_examples')}
        </Button>
      ),
    },
  ];

  const table = useReactTable({
    data: data?.items || [],
    rowCount: data?.count || 0,
    columns,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    // The API always sorts, so prevent the header from entering an unsorted state.
    enableSortingRemoval: false,
    state: {
      sorting,
      pagination,
    },
  });

  if (isError) {
    return (
      <CardContent className="pt-0">
        <div className="flex items-center justify-center py-12 text-destructive">
          {t('error_loading')}
        </div>
      </CardContent>
    );
  }

  if (isLoading) {
    return (
      <CardContent className="pt-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('table.article')}</TableHead>
              <TableHead>{t('table.type')}</TableHead>
              <TableHead className="text-right">{t('table.usage_count')}</TableHead>
              <TableHead className="text-right">{t('table.usage_share')}</TableHead>
              <TableHead className="text-center">{t('table.trend')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-24 mt-1" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-5 w-12 rounded-md" />
                </TableCell>
                <TableCell className="text-right">
                  <Skeleton className="h-4 w-10 ml-auto" />
                </TableCell>
                <TableCell className="text-right">
                  <Skeleton className="h-4 w-10 ml-auto" />
                </TableCell>
                <TableCell className="text-center">
                  <Skeleton className="h-10 w-36 rounded mx-auto" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-8 w-24 rounded" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    );
  }

  if (!data?.items.length) {
    return (
      <CardContent className="pt-0">
        <Empty className="py-12">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FileText className="size-5" />
            </EmptyMedia>
            <EmptyTitle>{t('no_data')}</EmptyTitle>
            <EmptyDescription>{t('no_data_hint')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </CardContent>
    );
  }

  return (
    <CardContent className="pt-0">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const isSortable = Boolean(header.column.columnDef.enableSorting);
                const sortDirection = header.column.getIsSorted();
                const tooltip = header.column.columnDef.meta?.tooltip;

                return (
                  <TableHead
                    key={header.id}
                    aria-sort={
                      isSortable
                        ? sortDirection === 'asc'
                          ? 'ascending'
                          : sortDirection === 'desc'
                            ? 'descending'
                            : 'none'
                        : undefined
                    }
                  >
                    <div
                      className={classNames('flex items-center gap-2', {
                        'justify-end': header.column.columnDef.meta?.align === 'right',
                        'justify-center': header.column.columnDef.meta?.align === 'center',
                      })}
                    >
                      {isSortable ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="group inline-flex cursor-pointer select-none items-center gap-2 rounded-sm bg-transparent p-0 text-inherit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                        >
                          <span aria-hidden="true">
                            {{
                              asc: <SortAscIcon className="size-4" />,
                              desc: <SortDescIcon className="size-4" />,
                            }[sortDirection as string] || (
                              <SortDescIcon className="size-4 text-muted-foreground opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100" />
                            )}
                          </span>
                          <span>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                        </button>
                      ) : (
                        <span>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                      )}
                      {typeof tooltip === 'string' && (
                        <TooltipProvider>
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger
                              type="button"
                              data-testid={`kb-sources-header-tooltip-${header.column.id}`}
                              className="cursor-help rounded-sm bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                            >
                              <Info className="size-3.5" aria-hidden="true" />
                              <span className="sr-only">{tooltip}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              {tooltip}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  style={{
                    width: cell.column.getSize() || 'auto',
                  }}
                  className={classNames({
                    'text-right': cell.column.columnDef.meta?.align === 'right',
                    'text-center': cell.column.columnDef.meta?.align === 'center',
                    'text-left': !cell.column.columnDef.meta?.align,
                  })}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-center pt-6 gap-3">
          <Pagination>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {t('previous')}
              </PaginationPrevious>
            </PaginationItem>

            <PaginationContent>
              {pages.map((page, idx) =>
                page === '...' ? (
                  <PaginationEllipsis key={`ellipsis-${idx}`}>...</PaginationEllipsis>
                ) : (
                  <PaginationItem
                    key={page}
                    onClick={() => setPagination((old) => ({ ...old, pageIndex: page - 1 }))}
                  >
                    <PaginationButton size="icon" isActive={page === currentPage}>
                      {page}
                    </PaginationButton>
                  </PaginationItem>
                ),
              )}
            </PaginationContent>

            <PaginationItem>
              <PaginationNext onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                {t('next')}
              </PaginationNext>
            </PaginationItem>
          </Pagination>
        </div>
      )}
    </CardContent>
  );
};
